import React, { ChangeEvent, useRef, useState } from 'react';

import { Button } from '@aicon-dev/caas-ui';
import { useTranslation } from 'react-i18next';

import { Sheet } from '../Sheet';

import { useUploadCall } from './hooks/useUpload';
import { UploadProps, UploadRenderProps } from './types';
import { UploadBasic } from './UploadBasic';
import { UploadPictureList } from './UploadPictureList';

export const Upload = (props: UploadProps) => {
  const {
    fileList,
    multiple = false,
    accept,
    onlyImage,
    listType = 'basic',
    children,
    render,
    block,
  } = props;
  const { t } = useTranslation('global');
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [openSelect, setOpenSelect] = useState(false);

  const {
    isLoading,
    uploadTempsResponses: {
      fileTemps,
      appendFileTemps,
      removeFileTemps,
      resetFileTemps,
      moveFileTemps,
    },
  } = useUploadCall(props);

  const handleClickButton = () => {
    inputFileRef.current?.click();
  };

  const renderUploadButton = () => {
    return (
      <Button type="button" onClick={handleClickButton} block={block}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            width: 16,
            height: 16,
          }}
        >
          <path
            d="M7.49648 10.3239C7.49648 10.6828 7.7875 10.9739 8.14648 10.9739C8.50547 10.9739 8.79648 10.6828 8.79648 10.3239L7.49648 10.3239ZM8.79648 10.3239L8.79648 1.39112L7.49648 1.39112L7.49648 10.3239L8.79648 10.3239Z"
            fill="#404040"
          />
          <path
            d="M11.5155 4.36926L8.14653 1.00052L4.77756 4.36926"
            stroke="#404040"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M1 9.93408V12.6138C1 13.6006 1.79989 14.4005 2.78661 14.4005H13.5067C14.4934 14.4005 15.2933 13.6006 15.2933 12.6138V9.93408"
            stroke="#404040"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
        {t('global:file_upload')}
      </Button>
    );
  };

  const renderProps: UploadRenderProps = {
    fields: fileTemps,
    record: fileList,
    isLoading,
    trigger: children
      ? React.cloneElement(children, {
          onClick: handleClickButton,
        })
      : renderUploadButton(),
    append: handleClickButton,
    remove: removeFileTemps,
    move: moveFileTemps,
    reset: resetFileTemps,
  };

  const renderDefaultTemplate = () => {
    if (listType === 'picture') return <UploadPictureList {...renderProps} />;
    return <UploadBasic {...renderProps} />;
  };

  const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const covertedFiles = Array.from(e.target.files ?? []);
    appendFileTemps(covertedFiles, e);
  };

  const handleOpenCamera = () => {
    if (!inputFileRef.current) return;
    inputFileRef.current.capture = 'camera';
    inputFileRef.current.click();
  };

  const handleOpenGallery = () => {
    if (!inputFileRef.current) return;
    if (inputFileRef.current.capture) {
      inputFileRef.current.removeAttribute('capture');
    }
    inputFileRef.current.click();
  };

  return (
    <div>
      <input
        hidden
        type="file"
        ref={inputFileRef}
        accept={accept ?? onlyImage ? 'image/png, image/jpeg' : ''}
        multiple={multiple}
        onChange={handleChangeInput}
      />
      <Sheet visible={openSelect} onClose={() => setOpenSelect(false)}>
        <Sheet.Menu>
          <Sheet.Menu.Item
            prefix={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M19.1673 15.8333C19.1673 16.2754 18.9917 16.6993 18.6792 17.0118C18.3666 17.3244 17.9427 17.5 17.5007 17.5H2.50065C2.05862 17.5 1.6347 17.3244 1.32214 17.0118C1.00958 16.6993 0.833984 16.2754 0.833984 15.8333V6.66667C0.833984 6.22464 1.00958 5.80072 1.32214 5.48816C1.6347 5.17559 2.05862 5 2.50065 5H5.83398L7.50065 2.5H12.5007L14.1673 5H17.5007C17.9427 5 18.3666 5.17559 18.6792 5.48816C18.9917 5.80072 19.1673 6.22464 19.1673 6.66667V15.8333Z"
                  stroke="#404040"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.99935 14.1667C11.8403 14.1667 13.3327 12.6743 13.3327 10.8333C13.3327 8.99238 11.8403 7.5 9.99935 7.5C8.1584 7.5 6.66602 8.99238 6.66602 10.8333C6.66602 12.6743 8.1584 14.1667 9.99935 14.1667Z"
                  stroke="#404040"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            onClick={handleOpenCamera}
          >
            카메라로 사진 촬영
          </Sheet.Menu.Item>

          <Sheet.Menu.Item
            prefix={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M1 14.9092L5.33156 10.0001L7.9305 12.4547L11.3958 8.36377L15.7273 13.2729"
                  stroke="#404040"
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                />
                <rect
                  x="1"
                  y="1"
                  width="14.7273"
                  height="14.7273"
                  rx="1.14546"
                  stroke="#404040"
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.63455 5.09096C6.63455 5.94306 5.94379 6.63382 5.09169 6.63382C4.23959 6.63382 3.54883 5.94306 3.54883 5.09096C3.54883 4.23886 4.23959 3.5481 5.09169 3.5481C5.94379 3.5481 6.63455 4.23886 6.63455 5.09096Z"
                  stroke="#404040"
                  strokeWidth="1.3"
                />
                <path
                  d="M15.728 5.12988H17.8553C18.4879 5.12988 19.0008 5.64272 19.0008 6.27534V17.8546C19.0008 18.4872 18.4879 19.0001 17.8553 19.0001H5.4189C4.78628 19.0001 4.27344 18.4872 4.27344 17.8546V15.7273"
                  stroke="#404040"
                  strokeWidth="1.3"
                  strokeLinejoin="round"
                />
              </svg>
            }
            onClick={handleOpenGallery}
          >
            앨범에서 사진 선택
          </Sheet.Menu.Item>
        </Sheet.Menu>
      </Sheet>
      {render ? render(renderProps) : renderDefaultTemplate()}
    </div>
  );
};
